import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { QRCodeSVG } from 'qrcode.react';

import {
  archiveClass,
  createClass,
  deleteClass,
  getClassDetail,
  listMyClasses,
  removeStudent,
  teacherJoinClass,
  updateClass,
} from '@/api/classes';
import { ClassDetailDialog } from '@/components/class/class-detail-dialog';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { Input } from '@/components/ui/input/input';

import type { ClassroomOut, ClassroomStudent } from '@/types';

import styles from './classroom.module.css';

function copy(text: string) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch {
    /* ignore */
  }
  document.body.removeChild(ta);
  return Promise.resolve();
}

function formatJoined(iso: string): string {
  if (!iso) return '-';
  return iso.replace('T', ' ').slice(0, 16);
}

function statusLabel(status: string): string {
  return status === 'archived' ? '已归档' : '正常';
}

export function ClassroomPage() {
  const { data: classes, isLoading, refetch } = useQuery({
    queryKey: ['my-classes'],
    queryFn: () => listMyClasses(),
  });

  const [filter, setFilter] = useState<'all' | 'active' | 'archived'>('all');
  const filtered = (classes ?? []).filter((c) => filter === 'all' || c.status === filter);

  const [expandedId, setExpandedId] = useState<number | null>(null);

  // 新建弹窗
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newError, setNewError] = useState('');
  const [creating, setCreating] = useState(false);

  // 编辑弹窗
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ClassroomOut | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // 删除确认
  const [deleteTarget, setDeleteTarget] = useState<ClassroomOut | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 查看学生
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [studentsList, setStudentsList] = useState<ClassroomStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsTarget, setStudentsTarget] = useState<ClassroomOut | null>(null);

  // 归档 / 删除 忙碌
  const [busyId, setBusyId] = useState<number | null>(null);

  // 班级详情（公告 / 排行榜 / 资料区）
  const [detailTarget, setDetailTarget] = useState<ClassroomOut | null>(null);

  // 协作加入班级
  const [joinTeacherOpen, setJoinTeacherOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [joining, setJoining] = useState(false);

  // toast
  const [toast, setToast] = useState('');
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 2000);
  };

  const resetNew = () => {
    setNewName('');
    setNewDesc('');
    setNewError('');
  };

  const openNew = () => {
    resetNew();
    setNewOpen(true);
  };

  const submitNew = async () => {
    if (!newName.trim()) {
      setNewError('班级名称不能为空');
      return;
    }
    setCreating(true);
    try {
      await createClass({ name: newName.trim(), description: newDesc.trim() });
      setNewOpen(false);
      showToast('班级创建成功');
      refetch();
    } catch (err) {
      setNewError(err instanceof Error ? err.message : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (c: ClassroomOut) => {
    setEditTarget(c);
    setEditName(c.name);
    setEditDesc(c.description ?? '');
    setEditError('');
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    if (!editName.trim()) {
      setEditError('班级名称不能为空');
      return;
    }
    setSavingEdit(true);
    try {
      await updateClass(editTarget.id, {
        name: editName.trim(),
        description: editDesc.trim(),
      });
      setEditOpen(false);
      showToast('已保存');
      refetch();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSavingEdit(false);
    }
  };

  const doArchive = async (c: ClassroomOut, archived: boolean) => {
    setBusyId(c.id);
    try {
      await archiveClass(c.id, archived);
      showToast(archived ? '已归档' : '已取消归档');
      refetch();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '操作失败');
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteClass(deleteTarget.id);
      setDeleteTarget(null);
      showToast('已删除');
      if (expandedId === deleteTarget.id) setExpandedId(null);
      refetch();
    } finally {
      setDeleting(false);
    }
  };

  const openStudents = async (c: ClassroomOut) => {
    setStudentsTarget(c);
    setStudentsOpen(true);
    setStudentsLoading(true);
    try {
      const detail = await getClassDetail(c.id);
      setStudentsList(detail.students ?? []);
    } finally {
      setStudentsLoading(false);
    }
  };

  const handleRemoveStudent = async (row: ClassroomStudent) => {
    if (!studentsTarget) return;
    if (!window.confirm(`确定将「${row.display_name}」移出班级吗？`)) return;
    try {
      await removeStudent(studentsTarget.id, row.id);
      setStudentsList((list) => list.filter((s) => s.id !== row.id));
      refetch();
      showToast('已移除');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '移除失败');
    }
  };

  const handleCopy = async (text: string) => {
    await copy(text);
    showToast('复制成功');
  };

  const submitTeacherJoin = async () => {
    if (!joinCode.trim()) {
      setJoinError('请输入班级邀请码');
      return;
    }
    setJoining(true);
    try {
      const joined = await teacherJoinClass(joinCode.trim());
      setJoinTeacherOpen(false);
      setJoinCode('');
      setJoinError('');
      showToast(`已加入班级「${joined.name}」`);
      refetch();
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : '加入失败');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>班级管理</h1>
          <p className={styles.subtitle}>
            创建班级后，把邀请码 / 邀请链接 / 二维码发给学生，学生即可加入并查看布置的作业。
            其他老师也可以用邀请码以协作身份加入，共同管理班级；每位老师只能批改自己发布的作业。
          </p>
        </div>
        <div className={styles.headerBtns}>
          <Button variant="secondary" onClick={() => setJoinTeacherOpen(true)}>
            协作加入班级
          </Button>
          <Button onClick={openNew}>新建班级</Button>
        </div>
      </header>

      <div className={styles.toolbar}>
        <span className={styles.filterLabel}>状态筛选</span>
        <select
          className={styles.select}
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
        >
          <option value="all">全部班级</option>
          <option value="active">正常班级</option>
          <option value="archived">已归档班级</option>
        </select>
        <span className={styles.count}>共 {filtered.length} 个班级</span>
      </div>

      {isLoading && <div className={styles.empty}>加载中...</div>}

      {!isLoading && (!classes || classes.length === 0) && (
        <div className={styles.emptyState}>
          <svg viewBox="0 0 200 160" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="30" y="40" width="140" height="95" rx="8" fill="#eef3fb" stroke="#c9d8ee" strokeWidth="2" />
            <rect x="48" y="58" width="104" height="10" rx="3" fill="#d6e3f7" />
            <rect x="48" y="78" width="80" height="8" rx="3" fill="#e2ebf8" />
            <rect x="48" y="96" width="90" height="8" rx="3" fill="#e2ebf8" />
            <circle cx="150" cy="118" r="26" fill="#165dff" opacity="0.12" />
            <path d="M150 106v24M138 118h24" stroke="#165dff" strokeWidth="3" strokeLinecap="round" />
          </svg>
          <div className={styles.emptyText}>
            还没有创建任何班级，创建后邀请学生加入即可开始布置作业。
          </div>
          <Button onClick={openNew}>快速创建班级</Button>
        </div>
      )}

      {!isLoading && classes && classes.length > 0 && filtered.length === 0 && (
        <div className={styles.empty}>当前筛选下暂无班级。</div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className={styles.grid}>
          {filtered.map((c) => (
            <Card key={c.id} className={styles.classCard}>
              <div className={styles.classHead}>
                <span className={styles.className}>{c.name}</span>
                <span
                  className={
                    c.status === 'archived' ? styles.tagArchived : styles.tagActive
                  }
                >
                  {statusLabel(c.status)}
                </span>
              </div>
              <div className={styles.classDesc}>{c.description || '暂无简介'}</div>

              <div className={styles.teacherTeam}>
                教师团队：
                {(c.teachers ?? []).length > 0
                  ? (c.teachers ?? [])
                      .map((t) => t.display_name + (t.is_owner ? '（创建者）' : ''))
                      .join('、')
                  : c.teacher_name || '—'}
              </div>

              <div className={styles.stats}>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{c.member_count}</div>
                  <div className={styles.statLab}>学生人数</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{c.assignment_count}</div>
                  <div className={styles.statLab}>布置作业</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{c.announcement_count ?? 0}</div>
                  <div className={styles.statLab}>班级公告</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statNum}>{c.material_count ?? 0}</div>
                  <div className={styles.statLab}>班级资料</div>
                </div>
              </div>

              <div className={styles.cardActions}>
                <Button variant="primary" size="small" onClick={() => setDetailTarget(c)}>
                  公告 / 排行榜 / 资料
                </Button>
                <Button variant="secondary" size="small" onClick={() => openStudents(c)}>
                  查看学生
                </Button>
                <Button variant="secondary" size="small" onClick={() => handleCopy(c.invite_link)}>
                  复制邀请
                </Button>
                <Button variant="secondary" size="small" onClick={() => openEdit(c)}>
                  编辑
                </Button>
                {c.status === 'active' ? (
                  <Button
                    variant="secondary"
                    size="small"
                    loading={busyId === c.id}
                    onClick={() => doArchive(c, true)}
                  >
                    归档
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="small"
                    loading={busyId === c.id}
                    onClick={() => doArchive(c, false)}
                  >
                    取消归档
                  </Button>
                )}
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => setDeleteTarget(c)}
                >
                  删除
                </Button>
              </div>

              <div
                className={styles.expandToggle}
                onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
              >
                {expandedId === c.id ? '收起邀请信息 ▲' : '展开邀请信息 ▼'}
              </div>

              {expandedId === c.id && (
                <div className={styles.inviteArea}>
                  <div className={styles.inviteRow}>
                    <span className={styles.inviteLabel}>邀请码</span>
                    <span className={styles.inviteVal}>{c.invite_code}</span>
                    <Button variant="secondary" size="small" onClick={() => handleCopy(c.invite_code)}>
                      复制
                    </Button>
                  </div>
                  <div className={styles.inviteRow}>
                    <span className={styles.inviteLabel}>邀请链接</span>
                    <span className={styles.inviteVal}>{c.invite_link}</span>
                    <Button variant="secondary" size="small" onClick={() => handleCopy(c.invite_link)}>
                      复制
                    </Button>
                  </div>
                  <div className={styles.qrBox}>
                    <div className={styles.qrInner}>
                      <QRCodeSVG value={c.invite_link} size={120} />
                    </div>
                    <div className={styles.qrTip}>学生扫码或访问链接即可加入</div>
                  </div>
                  {c.status === 'archived' && (
                    <div className={styles.archivedNote}>
                      已归档：学生无法新加入，但仍可查看历史数据。
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* 新建班级弹窗 */}
      {newOpen && (
        <div className={styles.overlay} onClick={() => setNewOpen(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>新建班级</div>
            <div className={styles.dialogBody}>
              <Input
                label="班级名称"
                value={newName}
                error={newError || undefined}
                onChange={(e) => {
                  setNewName(e.target.value);
                  if (newError) setNewError('');
                }}
                placeholder="例如：初一(3)班"
              />
              <label className={styles.fieldLabel}>
                班级简介
                <textarea
                  className={styles.textarea}
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  rows={3}
                  placeholder="选填，简单介绍班级用途"
                />
              </label>
            </div>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" onClick={() => setNewOpen(false)}>
                取消
              </Button>
              <Button onClick={submitNew} loading={creating}>
                确定
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑班级弹窗 */}
      {editOpen && (
        <div className={styles.overlay} onClick={() => setEditOpen(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>编辑班级</div>
            <div className={styles.dialogBody}>
              <Input
                label="班级名称"
                value={editName}
                error={editError || undefined}
                onChange={(e) => {
                  setEditName(e.target.value);
                  if (editError) setEditError('');
                }}
              />
              <label className={styles.fieldLabel}>
                班级简介
                <textarea
                  className={styles.textarea}
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={3}
                  placeholder="选填，简单介绍班级用途"
                />
              </label>
            </div>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" onClick={() => setEditOpen(false)}>
                取消
              </Button>
              <Button onClick={submitEdit} loading={savingEdit}>
                保存
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 删除二次确认 */}
      {deleteTarget && (
        <div className={styles.overlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>删除班级</div>
            <div className={styles.dialogBody}>
              <p className={styles.confirmText}>
                此操作将永久删除「{deleteTarget.name}」，且不可恢复。确定继续吗？
              </p>
            </div>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
                取消
              </Button>
              <Button variant="danger" loading={deleting} onClick={confirmDelete}>
                确定删除
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 查看学生弹窗 */}
      {studentsOpen && (
        <div className={styles.overlay} onClick={() => setStudentsOpen(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              {(studentsTarget?.name ?? '') + ' · 学生列表'}
            </div>
            <div className={styles.dialogBody}>
              {studentsTarget?.status === 'archived' && (
                <div className={styles.archivedNote}>
                  已归档班级的历史数据只读，不可移除学生。
                </div>
              )}
              {studentsLoading ? (
                <div className={styles.empty}>加载中...</div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>学生姓名</th>
                      <th>账号</th>
                      <th>加入时间</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsList.length === 0 && (
                      <tr>
                        <td colSpan={4} className={styles.empty}>
                          暂无学生，分享邀请让学生加入吧
                        </td>
                      </tr>
                    )}
                    {studentsList.map((s) => (
                      <tr key={s.id}>
                        <td>{s.display_name}</td>
                        <td>{s.username}</td>
                        <td>{formatJoined(s.joined_at)}</td>
                        <td>
                          {studentsTarget?.status === 'archived' ? (
                            <span className={styles.readonly}>—</span>
                          ) : (
                            <Button
                              variant="danger"
                              size="small"
                              onClick={() => handleRemoveStudent(s)}
                            >
                              移除
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" onClick={() => setStudentsOpen(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 协作加入班级弹窗 */}
      {joinTeacherOpen && (
        <div className={styles.overlay} onClick={() => setJoinTeacherOpen(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>协作加入班级</div>
            <div className={styles.dialogBody}>
              <Input
                label="班级邀请码"
                value={joinCode}
                error={joinError || undefined}
                onChange={(e) => {
                  setJoinCode(e.target.value.toUpperCase());
                  if (joinError) setJoinError('');
                }}
                placeholder="请输入 6 位邀请码，如 MATH03"
              />
              <p className={styles.confirmText}>
                以协作教师身份加入后，你可以发布公告、上传资料、查看排行榜，
                并在班级中布置自己的作业；每位老师只能批改自己发布的作业。
              </p>
            </div>
            <div className={styles.dialogFooter}>
              <Button variant="secondary" onClick={() => setJoinTeacherOpen(false)}>
                取消
              </Button>
              <Button onClick={submitTeacherJoin} loading={joining}>
                加入班级
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 班级详情：公告 / 排行榜 / 资料区 */}
      {detailTarget && (
        <ClassDetailDialog
          classroom={detailTarget}
          isTeacher
          onClose={() => setDetailTarget(null)}
        />
      )}

      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
}
